import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, CheckCircle, Clock, Filter, ShieldAlert, RefreshCw, 
  User, Calendar, FileText, Search, MapPin, Wrench, ShieldCheck, Check,
  ChevronLeft, ChevronRight 
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { useAuth } from '@/App';

const WardenLoggedIssues = () => {
  const { user } = useAuth();
  const hostelId = user?.hostel_id;

  const [issues, setIssues] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWarden, setSelectedWarden] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({ total: 0, pending: 0, in_progress: 0, resolved: 0 });

  // Resolution modal state
  const [resolveModal, setResolveModal] = useState({
    isOpen: false,
    issueId: null,
    issueTitle: '',
    status: 'resolved',
    note: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/wardens/?hostel_id=${hostelId}`);
      setWardens(response.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchIssues = useCallback(async (targetPage = page, targetSearch = search) => {
    if (!hostelId) return;
    setLoading(true);
    try {
      let url = `/v1/admin/warden-issues/?hostel_id=${hostelId}&page=${targetPage}&limit=${limit}`;
      if (selectedWarden) url += `&warden_id=${selectedWarden}`;
      if (selectedStatus) url += `&status=${selectedStatus}`;
      if (targetSearch.trim()) url += `&search=${encodeURIComponent(targetSearch.trim())}`;

      const response = await apiClient.get(url);
      const data = response.data;
      if (data) {
        setIssues(data.issues || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.total_pages || 1);
        if (data.counts) {
          setCounts(data.counts);
        }
      }
    } catch (err) {
      toast.error('Failed to load warden issues');
    } finally {
      setLoading(false);
    }
  }, [hostelId, selectedWarden, selectedStatus, page, limit, search]);

  useEffect(() => {
    if (hostelId) {
      fetchWardens();
    }
  }, [hostelId]);

  useEffect(() => {
    if (hostelId) {
      fetchIssues(page, search);
    }
  }, [hostelId, selectedWarden, selectedStatus, page, limit]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchIssues(1, search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const handleWardenFilterChange = (wId) => {
    setSelectedWarden(wId);
    setPage(1);
  };

  const handleStatusFilterChange = (st) => {
    setSelectedStatus(st);
    setPage(1);
  };

  const handleOpenResolveModal = (issue, status = 'resolved') => {
    setResolveModal({
      isOpen: true,
      issueId: issue.id,
      issueTitle: issue.title,
      status,
      note: issue.resolution_note || ''
    });
  };

  const handleConfirmResolve = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post(`/v1/admin/warden-issues/resolve/${resolveModal.issueId}`, {
        status: resolveModal.status,
        note: resolveModal.note || `Issue updated to ${resolveModal.status} by Admin`
      });
      toast.success(`Issue marked as ${resolveModal.status}`);
      setResolveModal({ isOpen: false, issueId: null, issueTitle: '', status: 'resolved', note: '' });
      fetchIssues(page, search);
    } catch (err) {
      toast.error('Failed to update issue status');
    } finally {
      setSubmitting(false);
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'urgent':
      case 'critical':
        return <Badge className="bg-rose-600 text-white font-semibold">Urgent</Badge>;
      case 'high':
        return <Badge className="bg-amber-500 text-white font-semibold">High</Badge>;
      case 'medium':
      case 'normal':
      case 'low':
      default:
        return (
          <Badge variant="outline" className="text-slate-600 font-semibold">
            {severity && !['medium', 'low'].includes(severity.toLowerCase()) ? severity : 'Normal'}
          </Badge>
        );
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" /> PENDING</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-600 text-white gap-1"><Wrench className="w-3 h-3" /> IN PROGRESS</Badge>;
      case 'resolved':
        return <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle className="w-3 h-3" /> RESOLVED</Badge>;
      default:
        return <Badge variant="outline">{status?.toUpperCase() || 'LOGGED'}</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="admin-warden-issues">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-amber-600" />
            Warden Logged Issues & Incident Reports
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Review maintenance requests, disciplinary reports, and concerns logged directly by hostel wardens
          </p>
        </div>
        <Button onClick={() => fetchIssues(page, search)} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh List
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Card className="bg-slate-50/70 border-slate-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Reports</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{counts.total || 0}</h3>
            </div>
            <FileText className="w-7 h-7 text-slate-500" />
          </CardContent>
        </Card>

        <Card className="bg-amber-50/50 border-amber-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Pending Action</p>
              <h3 className="text-2xl font-bold text-amber-900 mt-1">{counts.pending || 0}</h3>
            </div>
            <Clock className="w-7 h-7 text-amber-500" />
          </CardContent>
        </Card>

        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">In Progress</p>
              <h3 className="text-2xl font-bold text-blue-900 mt-1">{counts.in_progress || 0}</h3>
            </div>
            <Wrench className="w-7 h-7 text-blue-500" />
          </CardContent>
        </Card>

        <Card className="bg-emerald-50/50 border-emerald-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Resolved</p>
              <h3 className="text-2xl font-bold text-emerald-900 mt-1">{counts.resolved || 0}</h3>
            </div>
            <CheckCircle className="w-7 h-7 text-emerald-500" />
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter Bar */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search title, warden, student, room..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-semibold flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Warden:
              </span>
              <select
                className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                value={selectedWarden}
                onChange={(e) => handleWardenFilterChange(e.target.value)}
              >
                <option value="">All Wardens</option>
                {wardens.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-semibold">Status:</span>
              <select
                className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium"
                value={selectedStatus}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Issue List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading warden logged issues...</div>
        ) : issues.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
            <p className="text-slate-600 font-semibold">No logged issues found</p>
            <p className="text-slate-400 text-xs mt-1">
              {search || selectedWarden || selectedStatus ? 'No issues matching current filters.' : "All wardens' logs are clear or resolved."}
            </p>
          </Card>
        ) : (
          issues.map((issue) => (
            <Card key={issue.id} className="border border-slate-200 hover:shadow-md transition-all bg-white overflow-hidden">
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-slate-800 text-white">{issue.issue_type}</Badge>
                    {getSeverityBadge(issue.severity)}
                    {getStatusBadge(issue.status)}
                  </div>

                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(issue.created_at).toLocaleDateString()} {new Date(issue.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                </div>

                {/* Title & Description */}
                <div>
                  <h3 className="font-bold text-slate-900 text-base">{issue.title}</h3>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    {issue.description}
                  </p>
                </div>

                {/* Scope: Warden, Student, Room */}
                <div className="flex flex-wrap gap-4 text-xs text-slate-600 bg-slate-50/60 p-2.5 rounded-lg border border-slate-100">
                  <span className="flex items-center gap-1 font-semibold text-slate-800">
                    <FileText className="w-3.5 h-3.5 text-purple-600" />
                    Logged By Warden: {issue.warden_name || 'Assigned Warden'}
                  </span>

                  {issue.student_name && (
                    <span className="flex items-center gap-1 font-medium text-slate-700">
                      <User className="w-3.5 h-3.5 text-blue-600" />
                      Student: {issue.student_name} ({issue.class_name || 'N/A'})
                    </span>
                  )}

                  {issue.room_number && (
                    <span className="flex items-center gap-1 font-medium text-slate-700">
                      <MapPin className="w-3.5 h-3.5 text-amber-600" />
                      Location / Room: {issue.room_number}
                    </span>
                  )}
                </div>

                {/* Admin Resolution Remarks Banner */}
                {(issue.resolution_note || issue.status === 'resolved') && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 flex items-start gap-2.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-0.5 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-1">
                        <span className="font-bold text-emerald-800">🛡️ Admin Resolution & Remarks:</span>
                        {issue.resolved_by_name && (
                          <span className="text-[11px] text-emerald-600">
                            By: {issue.resolved_by_name} {issue.resolved_at ? `on ${new Date(issue.resolved_at).toLocaleDateString()}` : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-emerald-900 font-semibold bg-white/80 p-2 rounded-lg border border-emerald-200">
                        {issue.resolution_note || 'Resolved by Administrator.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Admin Action Buttons */}
                <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  {issue.status !== 'in_progress' && issue.status !== 'resolved' && (
                    <Button
                      onClick={() => handleOpenResolveModal(issue, 'in_progress')}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs font-semibold gap-1"
                    >
                      <Wrench className="w-3.5 h-3.5" /> Mark In Progress...
                    </Button>
                  )}

                  {issue.status !== 'resolved' && (
                    <Button
                      onClick={() => handleOpenResolveModal(issue, 'resolved')}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold gap-1"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Resolve Issue...
                    </Button>
                  )}

                  {issue.status === 'resolved' && (
                    <Button
                      onClick={() => handleOpenResolveModal(issue, 'resolved')}
                      variant="outline"
                      size="sm"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold gap-1"
                    >
                      <Check className="w-3.5 h-3.5" /> Edit Resolution Note
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-sm">
          <div className="flex flex-wrap items-center gap-2 text-slate-500 text-xs">
            <span>
              Showing <strong className="text-slate-800 font-bold">{Math.min((page - 1) * limit + 1, totalCount)}</strong> - <strong className="text-slate-800 font-bold">{Math.min(page * limit, totalCount)}</strong> of <strong className="text-slate-800 font-bold">{totalCount}</strong> reports
            </span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Show:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 font-medium outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || loading}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="h-8 px-2.5 text-xs text-slate-700 gap-1 border-slate-200 hover:bg-slate-50"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1))
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    return (
                      <React.Fragment key={p}>
                        {prev && p - prev > 1 && (
                          <span className="px-1 text-slate-400 text-xs">...</span>
                        )}
                        <button
                          onClick={() => setPage(p)}
                          disabled={loading}
                          className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${
                            page === p
                              ? 'bg-amber-600 text-white shadow-sm'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages || loading}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                className="h-8 px-2.5 text-xs text-slate-700 gap-1 border-slate-200 hover:bg-slate-50"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Resolution Note Modal Dialog */}
      <Dialog open={resolveModal.isOpen} onOpenChange={(open) => !open && setResolveModal(prev => ({ ...prev, isOpen: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              {resolveModal.status === 'resolved' ? 'Resolve Warden Issue' : 'Update Issue Progress'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleConfirmResolve} className="space-y-4 pt-2">
            <div>
              <p className="text-xs text-slate-500 mb-2">
                Issue: <strong className="text-slate-800">{resolveModal.issueTitle}</strong>
              </p>
              
              <label className="block text-xs font-bold text-slate-600 mb-1">
                Resolution Remarks / Action Taken *
              </label>
              <textarea
                rows={3}
                required
                placeholder="e.g. Electrician dispatched and replaced the broken fan capacitor. Issue fully resolved."
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                value={resolveModal.note}
                onChange={(e) => setResolveModal({ ...resolveModal, note: e.target.value })}
              />
            </div>

            <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setResolveModal(prev => ({ ...prev, isOpen: false }))}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className={resolveModal.status === 'resolved' ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold' : 'bg-blue-600 hover:bg-blue-700 text-white font-bold'}
              >
                {submitting ? 'Updating...' : `Confirm ${resolveModal.status === 'resolved' ? 'Resolution' : 'Update'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WardenLoggedIssues;
